import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { UserSettingsDocument, UserSettingsFormatting } from '../types/UserSettings';
import { authFetch } from '../lib/authFetch';

type SavePayload = {
  formatting?: Partial<UserSettingsFormatting>;
};

type ParsedResponse = {
  parsed: unknown;
  message: string;
};

type SettingsRequestDebug = {
  lastUrl: string;
  lastStatus: number | null;
  lastRequestId: string;
  lastLedgerMode: string;
  lastErrorMessage: string;
  lastUpdatedAt: number;
};

const DEFAULT_REQUEST_FAILURE = 'Request failed';
const MAX_RAW_ERROR_LENGTH = 300;

const readResponse = async (res: Response): Promise<ParsedResponse> => {
  const raw = await res.text();
  let parsed: unknown = null;
  try {
    parsed = raw ? JSON.parse(raw) : null;
  } catch {
    parsed = null;
  }

  const parsedMessage =
    parsed && typeof parsed === 'object' && typeof (parsed as { message?: unknown }).message === 'string'
      ? (parsed as { message: string }).message.trim()
      : '';
  const parsedError =
    parsed && typeof parsed === 'object' && typeof (parsed as { error?: unknown }).error === 'string'
      ? (parsed as { error: string }).error.trim()
      : '';
  const trimmedRaw = raw.trim();

  let message = DEFAULT_REQUEST_FAILURE;
  if (parsedMessage) {
    message = parsedMessage;
  } else if (parsedError) {
    message = parsedError;
  } else if (trimmedRaw) {
    message =
      trimmedRaw.length > MAX_RAW_ERROR_LENGTH
        ? `${trimmedRaw.slice(0, MAX_RAW_ERROR_LENGTH)}...`
        : trimmedRaw;
  }

  return { parsed, message };
};

export const useUserSettings = () => {
  const { auth } = useAuth();
  const [settings, setSettings] = useState<UserSettingsDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [debug, setDebug] = useState<SettingsRequestDebug>({
    lastUrl: '',
    lastStatus: null,
    lastRequestId: '',
    lastLedgerMode: '',
    lastErrorMessage: '',
    lastUpdatedAt: 0,
  });
  const loadRequestCounter = useRef(0);
  const saveRequestCounter = useRef(0);
  const loadAbortController = useRef<AbortController | null>(null);

  const updateDebugState = useCallback(
    (
      url: string,
      details?: {
        res?: Response;
        errorMessage?: string;
      }
    ) => {
      const status = details?.res ? details.res.status : null;
      const requestId = details?.res?.headers?.get('X-Request-Id') ?? '';
      const ledgerMode = details?.res?.headers?.get('X-Ledger-Mode') ?? '';
      setDebug({
        lastUrl: url,
        lastStatus: status,
        lastRequestId: requestId,
        lastLedgerMode: ledgerMode,
        lastErrorMessage: details?.errorMessage ?? '',
        lastUpdatedAt: Date.now(),
      });
    },
    []
  );

  const load = useCallback(async () => {
    const url = '/api/settings/me';
    if (!auth.token) {
      loadAbortController.current?.abort();
      setSettings(null);
      setError('You must be signed in to load settings.');
      updateDebugState(url, { errorMessage: 'You must be signed in to load settings.' });
      return;
    }

    loadAbortController.current?.abort();
    const requestCounter = ++loadRequestCounter.current;
    const controller = new AbortController();
    loadAbortController.current = controller;

    setLoading(true);
    setError('');
    updateDebugState(url);
    try {
      const res = await authFetch(url, {
        headers: { Authorization: `Bearer ${auth.token}` },
        signal: controller.signal,
      });
      const { parsed, message } = await readResponse(res);
      if (requestCounter !== loadRequestCounter.current) return;

      updateDebugState(url, { res });
      if (!res.ok) {
        const errorMessage = `[${res.status}] ${message}`;
        updateDebugState(url, { res, errorMessage });
        throw new Error(errorMessage);
      }

      if (!parsed || typeof parsed !== 'object') {
        const errorMessage = `[${res.status}] Invalid response payload`;
        updateDebugState(url, { res, errorMessage });
        throw new Error(errorMessage);
      }
      const data = parsed as UserSettingsDocument;
      setSettings(data);
    } catch (err) {
      if (requestCounter !== loadRequestCounter.current) return;
      if (err instanceof Error && err.name === 'AbortError') return;
      const message = err instanceof Error ? err.message : 'Unable to load settings.';
      setError(message);
      if (!message.startsWith('[')) {
        updateDebugState(url, { errorMessage: message });
      }
    } finally {
      if (requestCounter !== loadRequestCounter.current) return;
      setLoading(false);
    }
  }, [auth.token, updateDebugState]);

  useEffect(() => {
    load();
    return () => {
      loadAbortController.current?.abort();
    };
  }, [load]);

  const save = useCallback(
    async (payload: SavePayload) => {
      const url = '/api/settings/me';
      if (!auth.token) {
        const message = 'You must be signed in to update settings.';
        setSaveError(message);
        updateDebugState(url, { errorMessage: message });
        return null;
      }

      const requestCounter = ++saveRequestCounter.current;
      setSaving(true);
      setSaveError('');
      updateDebugState(url);
      try {
        const res = await authFetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.token}`,
          },
          body: JSON.stringify(payload),
        });
        const { parsed, message } = await readResponse(res);
        if (requestCounter !== saveRequestCounter.current) return null;

        updateDebugState(url, { res });

        if (!res.ok) {
          const errorMessage = `[${res.status}] ${message}`;
          updateDebugState(url, { res, errorMessage });
          throw new Error(errorMessage);
        }

        if (!parsed || typeof parsed !== 'object') {
          const errorMessage = `[${res.status}] Invalid response payload`;
          updateDebugState(url, { res, errorMessage });
          throw new Error(errorMessage);
        }

        const data = parsed as UserSettingsDocument;
        setSettings(data);
        return data;
      } catch (err) {
        if (requestCounter !== saveRequestCounter.current) return null;
        const message = err instanceof Error ? err.message : 'Unable to save settings.';
        setSaveError(message);
        if (!message.startsWith('[')) {
          updateDebugState(url, { errorMessage: message });
        }
        return null;
      } finally {
        if (requestCounter !== saveRequestCounter.current) return;
        setSaving(false);
      }
    },
    [auth.token, updateDebugState]
  );

  return {
    settings,
    loading,
    error,
    save,
    saving,
    saveError,
    reload: load,
    lastUrl: debug.lastUrl,
    lastStatus: debug.lastStatus,
    lastRequestId: debug.lastRequestId,
    lastLedgerMode: debug.lastLedgerMode,
    lastErrorMessage: debug.lastErrorMessage,
    lastUpdatedAt: debug.lastUpdatedAt,
  };
};

export default useUserSettings;
